import React, { useCallback, useState, useEffect } from "react";
import {
  Image,
  Form,
  Checkbox,
  Dropdown,
  Modal,
  Button,
} from "semantic-ui-react";
import { useFormik } from "formik";
import { initialValues, validationSchema } from "./StudentForm.form";
import { StudentAPI, InstitutionApi } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { image } from "../../../../assets";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

const studentController = new StudentAPI();
const institutionController = new InstitutionApi();

export function StudentForm(props) {
  const { close, onReload, student } = props;
  const { accessToken, user } = useAuth();
  const { t } = useTranslation(["students", "teachers", "common"]);
  const institutionId = user.user.institutions[0];

  // Estado para almacenar los datos de la institución
  const [institution, setInstitution] = useState(null);

  // Estado para mostrar/ocultar el modal de límite de estudiantes
  const [showLimitModal, setShowLimitModal] = useState(false);

  // Al montar el componente, obtenemos la institución (y con ella el array de students).
  useEffect(() => {
    const fetchInstitution = async () => {
      try {
        const response =
          await institutionController.getInstitutionsbyInstitution(
            accessToken,
            institutionId,
            true,
          );
        setInstitution(response.institution);
      } catch (error) {
        console.error(error);
      }
    };
    fetchInstitution();
  }, [accessToken, institutionId]);

  // Convertir el array de `classnumber` en opciones para el Dropdown.
  const classNumberOptions = (institution?.classnumber || []).map((cn) => ({
    key: cn,
    value: cn,
    text: cn,
  }));

  // Configuración de Formik
  const formik = useFormik({
    initialValues: initialValues(student),
    validationSchema: validationSchema(student, t),
    validateOnChange: false,
    onSubmit: async (formValue) => {
      try {
        // 1. Verificar el límite SOLO si estamos creando un nuevo estudiante
        if (!student && institution) {
          const currentStudentsCount = institution?.students?.length || 0;
          const maxStudentsAllowed = institution?.studentsnum || 0;

          if (currentStudentsCount >= maxStudentsAllowed) {
            setShowLimitModal(true); // Mostrar el modal de aviso
            return; // Cancelar la creación
          }
        }

        // 2. Construir formData con los datos del formulario
        const formData = new FormData();
        formData.append("name", formValue.firstname);
        formData.append("surname", formValue.lastname);
        formData.append("email", formValue.email);

        if (formValue.password) {
          formData.append("password", formValue.password);
        }
        formData.append("active", formValue.active);

        if (formValue.classnumber) {
          formData.append("classnumber", formValue.classnumber);
        }

        // Avatar si se ha seleccionado
        if (formValue.fileAvatar) {
          formData.append("avatar", formValue.fileAvatar);
        }

        // 3. Crear nuevo o actualizar existente
        if (!student) {
          // Crear nuevo student
          await studentController.createStudent(
            accessToken,
            institutionId,
            formData,
          );
        } else {
          // Actualizar un student existente
          await studentController.updateStudent(
            accessToken,
            institutionId,
            student._id,
            formData,
          );
        }

        // Cerrar y recargar
        if (onReload) onReload();
        if (close) close();
      } catch (error) {
        console.error(
          "Error durante la creación/actualización del estudiante:",
          error,
        );
      }
    },
  });

  // Manejo de dropzone para subir avatar
  const onDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        const validMimeTypes = ["image/jpeg", "image/png"];
        if (validMimeTypes.includes(file.type)) {
          formik.setFieldValue("avatar", URL.createObjectURL(file));
          formik.setFieldValue("fileAvatar", file);
        } else {
          console.error("Formato de archivo inválido. Debe ser JPG o PNG.");
        }
      }
    },
    [formik],
  );

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/jpeg": [".jpeg", ".jpg"],
      "image/png": [".png"],
    },
    onDrop,
    multiple: false,
  });

  // Obtener la imagen que se mostrará en el avatar
  const getAvatar = () => {
    if (formik.values.fileAvatar) {
      return formik.values.avatar;
    } else if (formik.values.avatar) {
      return formik.values.avatar;
    }
    return image.noAvatar;
  };

  // Modal de aviso cuando se supera el límite de estudiantes
  const LimitReachedModal = (
    <Modal
      open={showLimitModal}
      onClose={() => setShowLimitModal(false)}
      size="small"
    >
      <Modal.Header>{t("students:modal.limitTitle")}</Modal.Header>
      <Modal.Content>
        <p>{t("students:modal.limitContent1")}</p>
        <p>{t("students:modal.limitContent2")}</p>
      </Modal.Content>
      <Modal.Actions>
        <Button primary onClick={() => setShowLimitModal(false)}>
          {t("students:modal.closeButton")}
        </Button>
      </Modal.Actions>
    </Modal>
  );

  return (
    <>
      {LimitReachedModal}

      <Form className="user-form" onSubmit={formik.handleSubmit}>
        <div className="user-form__avatar" {...getRootProps()}>
          <input {...getInputProps()} />
          <Image avatar size="small" src={getAvatar()} />
        </div>

        <Form.Group widths="equal">
          <Form.Input
            name="firstname"
            placeholder={t("teachers:form.firstnamePlaceholder")}
            onChange={formik.handleChange}
            value={formik.values.firstname}
            error={formik.errors.firstname}
          />
          <Form.Input
            name="lastname"
            placeholder={t("teachers:form.lastnamePlaceholder")}
            onChange={formik.handleChange}
            value={formik.values.lastname}
            error={formik.errors.lastname}
          />
        </Form.Group>

        <Form.Input
          name="email"
          placeholder={t("teachers:form.emailPlaceholder")}
          onChange={formik.handleChange}
          value={formik.values.email}
          error={formik.errors.email}
          autoComplete="off"
        />

        <Form.Input
          type="password"
          name="password"
          placeholder={t("teachers:form.passwordPlaceholder")}
          onChange={formik.handleChange}
          value={formik.values.password}
          error={formik.errors.password}
          autoComplete="new-password"
        />

        <Form.Field>
          <Checkbox
            toggle
            label={t("students:form.activeLabel")}
            checked={formik.values.active}
            onChange={(_, data) => formik.setFieldValue("active", data.checked)}
          />
        </Form.Field>

        <Form.Field>
          <label>{t("students:form.selectClassLabel")}</label>
          <Dropdown
            placeholder={t("students:form.selectClassPlaceholder")}
            fluid
            selection
            options={classNumberOptions}
            name="classnumber"
            value={formik.values.classnumber || ""}
            onChange={(_, data) =>
              formik.setFieldValue("classnumber", data.value)
            }
          />
        </Form.Field>

        <Form.Button type="submit" primary fluid loading={formik.isSubmitting}>
          {student
            ? t("students:form.updateButton")
            : t("students:form.createButton")}
        </Form.Button>
      </Form>
    </>
  );
}
